import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
  Slice,
} from '@reduxjs/toolkit'
import { StatusType } from '@types'
import {
  STORE_STATUS_ERROR,
  STORE_STATUS_FINISHED,
  STORE_STATUS_LOADING,
} from 'constants/store'

export interface GetRejectValue<E = unknown> {
  code: string
  message: StatusType
  details?: E
}

export type StoreErrorType<E = unknown> = null | (Error & { details?: E })

export interface GenericState<T, E = unknown> {
  data: T
  status: StatusType
  error: StoreErrorType<E>
}

export type GenericReducers<T, E> = {
  start: (state: GenericState<T, E>) => void
  success: (state: GenericState<T, E>, action: PayloadAction<T>) => void
  error: (state: GenericState<T, E>, action: PayloadAction<Error>) => void
  clear: (state: GenericState<T, E>) => void
  clearError: (state: GenericState<T, E>) => void
}

export const createGenericSlice = <
  T,
  E,
  Reducers extends SliceCaseReducers<GenericState<T, E>>,
>({
  name = '',
  initialState,
  reducers,
  extraReducers,
}: {
  name: string
  initialState: GenericState<T, E>
  reducers: ValidateSliceCaseReducers<GenericState<T, E>, Reducers>
  extraReducers?: (builder: ActionReducerMapBuilder<GenericState<T>>) => void
}): Slice<GenericState<T, E>, GenericReducers<T, E> & Reducers, string> => {
  return createSlice({
    name,
    initialState,
    reducers: {
      start(state: GenericState<T, E>) {
        state.status = STORE_STATUS_LOADING
      },
      success(state: GenericState<T, E>, action: PayloadAction<T>) {
        state.data = action.payload
        state.status = STORE_STATUS_FINISHED
      },
      error(state: GenericState<T, E>, action: PayloadAction<Error>) {
        state.error = action.payload
        state.status = STORE_STATUS_ERROR
      },
      clear(state: GenericState<T, E>) {
        state.error = initialState.error
        state.data = initialState.data
        state.status = initialState.status
      },
      clearError(state: GenericState<T, E>) {
        state.error = initialState.error
        state.status = initialState.status
      },
      ...(reducers as any),
    },
    extraReducers,
  })
}
